




import React, { useState, useEffect } from 'react';
import backgroundImage from '../images/bo.png';
import birdBg from '../images/bird_background.png';
import Bird from '../components/Bird';

const Dashboard = () => {
  const [displayedText, setDisplayedText] = useState('');
  const [showSubtitle, setShowSubtitle] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [showGreenArrow, setShowGreenArrow] = useState(false);
  const [showWhiteArrow, setShowWhiteArrow] = useState(false);

  const mainText = "Structure your path.";
  const subtitle = "SynapSync is a mindful planning tool designed to bring clarity, focus, and direction to your days.";

  // Generate random floating letters with responsive adjustments
  const generateFloatingLetters = () => {
    const letters = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
    // Reduce number of letters on mobile
    const letterCount = window.innerWidth < 768 ? 30 : 60;
    
    return Array.from({ length: letterCount }, (_, i) => ({
      id: i,
      char: letters[Math.floor(Math.random() * letters.length)],
      x: Math.random() * 100,
      y: Math.random() * 100,
      delay: Math.random() * 8,
      duration: 8 + Math.random() * 12,
      size: window.innerWidth < 768 ? 8 + Math.random() * 8 : 10 + Math.random() * 12,
      moveX: (Math.random() - 0.5) * (window.innerWidth < 768 ? 40 : 60),
      moveY: (Math.random() - 0.5) * (window.innerWidth < 768 ? 30 : 40),
      rotate: Math.random() * 360,
      opacity: 0.1 + Math.random() * 0.15
    }));
  };

  const [floatingLetters, setFloatingLetters] = useState([]);

  useEffect(() => {
    // Initialize floating letters
    setFloatingLetters(generateFloatingLetters());

    // Regenerate letters on window resize
    const handleResize = () => {
      setFloatingLetters(generateFloatingLetters());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Show green arrow first
    const greenArrowTimer = setTimeout(() => {
      setShowGreenArrow(true);
    }, 500);

    // Start typing animation after green arrow
    const typingTimer = setTimeout(() => {
      let index = 0;
      const typeInterval = setInterval(() => {
        if (index <= mainText.length) {
          setDisplayedText(mainText.slice(0, index));
          index++;
        } else {
          clearInterval(typeInterval);
          // Show subtitle after main text is complete
          setTimeout(() => setShowSubtitle(true), 300);
          // Show button after subtitle
          setTimeout(() => setShowButton(true), 800);
          // Show white arrow last
          setTimeout(() => setShowWhiteArrow(true), 1200);
        }
      }, 80);
      return () => clearInterval(typeInterval);
    }, 1000);

    return () => {
      clearTimeout(greenArrowTimer);
      clearTimeout(typingTimer);
    };
  }, []);

  return (
    <div className="h-screen w-full snap-y snap-mandatory overflow-y-scroll scroll-smooth bg-[#2a2b2b] text-white">
      {/* Top Section */}
      <section className="h-screen relative">
        {/* Floating Letters Background */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {floatingLetters.map((letter) => (
            <div
              key={letter.id}
              className="absolute text-dark-gray floating-letter"
              style={{
                left: `${letter.x}%`,
                top: `${letter.y}%`,
                fontSize: `${letter.size}px`,
                opacity: letter.opacity,
                '--move-x': `${letter.moveX}px`,
                '--move-y': `${letter.moveY}px`,
                '--rotate': `${letter.rotate}deg`,
                '--duration': `${letter.duration}s`,
                '--delay': `${letter.delay}s`,
              }}
            >
              {letter.char}
            </div>
          ))}
        </div>

        {/* Main Content */}
        <div className="relative z-10 flex flex-col items-center justify-center min-h-[80vh] px-4 sm:px-6 lg:px-8">
          {/* Green Arrow - Responsive sizing */}
          <div className={`mb-4 sm:mb-6 md:mb-8 transition-all duration-1000 ease-out transform ${
            showGreenArrow ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
          }`}>
            <svg 
              width="60" 
              height="60" 
              viewBox="0 0 100 100" 
              className="text-green-400 sm:w-16 sm:h-16 md:w-20 md:h-20"
            >
              <path
                d="M15 85 L15 25 L75 50 Z"
                fill="currentColor"
                className="drop-shadow-lg"
              />
            </svg>
          </div>

          {/* Main Heading with Typewriter Effect - Responsive text sizing */}
          <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-center mb-4 sm:mb-6 md:mb-8 leading-tight px-2">
            <span className="inline-block">
              {displayedText}
              <span className="animate-blink">|</span>
            </span>
          </h1>

          {/* Subtitle with Fade In - Responsive text and spacing */}
          <div className={`max-w-xs sm:max-w-lg md:max-w-2xl text-center mb-8 sm:mb-10 md:mb-12 px-4 transition-all duration-1000 ease-out ${
            showSubtitle ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}>
            <p className="text-sm sm:text-base md:text-xl lg:text-2xl text-gray-300 leading-relaxed">
              {subtitle}
            </p>
          </div>
        </div>

        {/* White Arrow - Responsive positioning and sizing */}
        <div className={`fixed top-1/2 right-2 sm:right-4 md:right-6 lg:right-8 transform -translate-y-1/2 transition-all duration-1000 ease-out ${
          showWhiteArrow ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'
        }`}>
          <svg 
            width="40" 
            height="40" 
            viewBox="0 0 100 100" 
            className="text-white sm:w-12 sm:h-12 md:w-16 md:h-16 lg:w-20 lg:h-20"
          >
            <path
              d="M20 20 L80 50 L20 80 L30 50 Z"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {/* Bottom Background Image - Responsive height */}
        <div className="absolute bottom-0 left-0 w-full z-5">
          <img 
            src={backgroundImage} 
            alt="Bottom background" 
            className="w-full h-auto object-cover"
            style={{ 
              maxHeight: window.innerWidth < 768 ? '25vh' : '40vh',
              objectPosition: 'center bottom'
            }}
          />
        </div>
      </section>

      {/* Second Page / Background Section - Responsive background */}
      <section 
        className="h-screen relative flex items-center justify-center bg-cover bg-center bg-no-repeat" 
        style={{ 
          backgroundImage: `url(${birdBg})`,
          backgroundSize: window.innerWidth < 768 ? 'cover' : 'cover',
          backgroundPosition: window.innerWidth < 768 ? 'center center' : 'center'
        }}
      >
        <div className="w-full h-full flex items-center justify-center px-4">
          <Bird />
        </div>
      </section>

      {/* Footer - Responsive text and padding */}
      <footer className="bg-[#2a2b2b] text-gray-200 text-xs sm:text-sm text-center py-3 sm:py-4 mt-auto backdrop-blur-sm border-t border-gray-600 px-4">
        <p>© {new Date().getFullYear()} Academic Tracker • Built for a purpose</p>
      </footer>

      {/* Custom Styles - Enhanced with responsive considerations */}
      <style jsx>{`
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
        .animate-blink {
          animation: blink 1s infinite;
        }
        
        @keyframes floatAround {
          0% { 
            transform: translateX(0) translateY(0) rotate(0deg);
          }
          25% { 
            transform: translateX(var(--move-x)) translateY(calc(var(--move-y) * -1)) rotate(90deg);
          }
          50% { 
            transform: translateX(calc(var(--move-x) * -1)) translateY(var(--move-y)) rotate(180deg);
          }
          75% { 
            transform: translateX(var(--move-x)) translateY(calc(var(--move-y) * 0.5)) rotate(270deg);
          }
          100% { 
            transform: translateX(0) translateY(0) rotate(360deg);
          }
        }
        
        @keyframes fadeInOut {
          0%, 100% { opacity: var(--base-opacity); }
          50% { opacity: calc(var(--base-opacity) * 0.3); }
        }
        
        .floating-letter {
          --base-opacity: 0.15;
          animation: 
            floatAround var(--duration) ease-in-out infinite,
            fadeInOut calc(var(--duration) * 0.8) ease-in-out infinite;
          animation-delay: var(--delay), calc(var(--delay) + 1s);
          will-change: transform, opacity;
        }
        
        .floating-letter:hover {
          --base-opacity: 0.4;
          animation-duration: calc(var(--duration) * 0.5), calc(var(--duration) * 0.4);
        }
        
        /* Responsive adjustments for very small screens */
        @media (max-width: 480px) {
          .floating-letter {
            font-size: 6px !important;
            --move-x: 20px;
            --move-y: 15px;
          }
        }
        
        /* Disable animations on reduced motion preference */
        @media (prefers-reduced-motion: reduce) {
          .floating-letter {
            animation: none;
          }
          
          .animate-blink {
            animation: none;
          }
          
          * {
            transition-duration: 0.01ms !important;
            animation-duration: 0.01ms !important;
          }
        }
        
        .nav-tab {
          position: relative;
          display: inline-block;
          padding: 8px 16px;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          font-weight: 500;
          letter-spacing: 0.025em;
          overflow: hidden;
          font-size: 14px;
        }
        
        @media (min-width: 640px) {
          .nav-tab {
            padding: 10px 20px;
            border-radius: 8px;
            font-size: 16px;
          }
        }
        
        .nav-tab:hover {
          color: #ffffff;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }
        
        .nav-tab-bg {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.8), rgba(147, 51, 234, 0.8));
          border-radius: inherit;
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: -1;
        }
        
        .nav-tab:hover .nav-tab-bg {
          transform: scaleX(1);
        }
        
        .nav-tab::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: inherit;
          transition: border-color 0.3s ease;
          z-index: -1;
        }
        
        .nav-tab:hover::before {
          border-color: rgba(255, 255, 255, 0.3);
        }
      `}</style>
    </div>
  );
};

export default Dashboard;







